import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/context/auth-context'
import { useToast } from '@/hooks/use-toast'
import { User, Shield, Bell, Key, Loader2, Smartphone } from 'lucide-react'

export default function IndividualSettingsPage() {
  const { user } = useAuth()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const profile = user?.profile
  const firstName = profile?.firstName || ''
  const lastName = profile?.lastName || ''
  const phone = profile?.phone || ''
  const dateOfBirth = (profile as any)?.dateOfBirth || ''

  const handleSave = async () => {
    setIsLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))
      toast({
        title: "Settings saved",
        description: "Your settings have been updated successfully.",
      })
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Could not save settings. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your personal account settings</p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5 text-[#D64463]" />
            Personal Information
          </CardTitle>
          <CardDescription>Your personal details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">First Name</label>
              <Input value={firstName} disabled className="mt-1 bg-gray-50" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Last Name</label>
              <Input value={lastName} disabled className="mt-1 bg-gray-50" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Email Address</label>
            <Input value={user?.email || ''} disabled className="mt-1 bg-gray-50" />
          </div>
          {phone && (
            <div>
              <label className="text-sm font-medium text-gray-700">Phone Number</label>
              <Input value={phone} disabled className="mt-1 bg-gray-50" />
            </div>
          )}
          {dateOfBirth && (
            <div>
              <label className="text-sm font-medium text-gray-700">Date of Birth</label>
              <Input value={dateOfBirth} disabled className="mt-1 bg-gray-50" />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-[#D64463]" />
            Mobile App
          </CardTitle>
          <CardDescription>Manage your health records on the go</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gradient-to-r from-[#D64463]/10 to-purple-500/10 rounded-xl p-4 border border-[#D64463]/20">
            <p className="text-sm text-gray-700 mb-3">
              Download the CareVault mobile app to add health records, log symptoms, upload medical documents, and access your data anywhere.
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Smartphone className="h-4 w-4" />
              <span>Available on iOS and Android</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#D64463]" />
            Privacy & Data
          </CardTitle>
          <CardDescription>Control your health data privacy</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">Data Encryption</p>
              <p className="text-sm text-gray-500">Your health data is encrypted at rest</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">Provider Sharing</p>
              <p className="text-sm text-gray-500">Control which providers can access your records</p>
            </div>
            <Badge className="bg-blue-100 text-blue-700">Manage</Badge>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#D64463]" />
            Notifications
          </CardTitle>
          <CardDescription>Configure how you receive notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">Health Reminders</p>
              <p className="text-sm text-gray-500">Get reminders for appointments and medications</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">Enabled</Badge>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">Provider Access Alerts</p>
              <p className="text-sm text-gray-500">Notifications when providers view your records</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">Enabled</Badge>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="h-5 w-5 text-[#D64463]" />
            Security
          </CardTitle>
          <CardDescription>Manage your account security</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button variant="outline" className="w-full justify-start">
            Change Password
          </Button>
          <Button variant="outline" className="w-full justify-start">
            Enable Two-Factor Authentication
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={isLoading}
          className="bg-gradient-to-r from-[#D64463] to-[#9C27B0] hover:opacity-90"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  )
}
